/**
 * Drivers admin · /admin/drivers
 *
 * CRUD + generate-link + send-test-push for the driver roster.
 */
import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Trash, Copy, BellRinging, Truck, Clipboard } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Drivers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', carrier_company: '', notes: '' });
  const [lastCreated, setLastCreated] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/drivers`, { withCredentials: true });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error('Name is required');
      return;
    }
    try {
      const r = await axios.post(`${API}/drivers`, form, { withCredentials: true });
      toast.success('Driver saved');
      setLastCreated(r.data);
      setForm({ name: '', email: '', phone: '', carrier_company: '', notes: '' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.name}? This removes their push subscriptions too.`)) return;
    try {
      await axios.delete(`${API}/drivers/${d.id}`, { withCredentials: true });
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const testPush = async (d) => {
    try {
      const r = await axios.post(`${API}/drivers/${d.id}/push-test`, {}, { withCredentials: true });
      toast.success(`Push sent to ${r.data.sent} device(s) · ${r.data.failed} failed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Push test failed');
    }
  };

  const copy = (url) => navigator.clipboard.writeText(url).then(
    () => toast.success('Copied'),
    () => toast.error('Copy failed'),
  );

  return (
    <Layout>
      <div className="p-6" data-testid="drivers-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <p className="overline">Fleet Roster</p>
            <h1 className="text-3xl font-bold tracking-tight">Drivers</h1>
          </div>
          <Button onClick={() => setAddOpen(true)} data-testid="create-driver-btn"
            className="bg-brand text-white rounded-none hover:bg-[#0040d9]">
            <Plus size={16} className="mr-1" weight="bold" /> New Driver
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Company</th>
                <th className="text-right">Active Pickups</th>
                <th className="text-right">Devices</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-muted-foreground" data-testid="drivers-empty">
                  No drivers yet. Create one to start assigning pickups.
                </td></tr>
              ) : rows.map(d => (
                <tr key={d.id} data-testid={`driver-row-${d.id}`}>
                  <td className="font-bold">{d.name}</td>
                  <td className="font-mono text-xs">{d.email || '—'}</td>
                  <td className="font-mono text-xs">{d.phone || '—'}</td>
                  <td className="text-xs">{d.carrier_company || '—'}</td>
                  <td className="font-mono text-right font-bold">{d.active_pickups ?? 0}</td>
                  <td className="font-mono text-right">
                    {d.subscription_count > 0 ? (
                      <span className="inline-flex items-center gap-0.5 text-emerald-700 font-bold">
                        <BellRinging size={11} weight="fill" /> {d.subscription_count}
                      </span>
                    ) : <span className="text-muted-foreground">0</span>}
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button type="button" title="Test push"
                        onClick={() => testPush(d)}
                        disabled={!d.subscription_count}
                        data-testid={`driver-test-push-${d.id}`}
                        className="text-[10px] font-bold uppercase tracking-widest text-brand hover:text-[#0040d9] disabled:text-slate-300">
                        <BellRinging size={13} weight="bold" className="inline mr-0.5" /> Test
                      </button>
                      <button type="button" title="Delete" onClick={() => remove(d)}
                        data-testid={`driver-delete-${d.id}`}
                        className="text-rose-600 hover:text-rose-700">
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {lastCreated && (
          <div className="mt-4 bg-emerald-50 border border-emerald-300 p-3" data-testid="driver-last-created">
            <p className="overline text-emerald-800">Driver link ready — share with {lastCreated.name}</p>
            <div className="flex items-center gap-2 mt-1">
              <input readOnly value={lastCreated.public_url}
                className="flex-1 px-2 py-1 border border-emerald-300 bg-card font-mono text-xs" />
              <Button size="sm" onClick={() => copy(lastCreated.public_url)}
                data-testid="driver-copy-url" className="rounded-none bg-brand text-white">
                <Copy size={12} weight="bold" className="mr-1" /> Copy
              </Button>
              <a href={lastCreated.public_url} target="_blank" rel="noreferrer"
                className="text-xs font-bold uppercase tracking-widest px-3 py-2 border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white">
                Open
              </a>
            </div>
          </div>
        )}

        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="rounded-none" data-testid="create-driver-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck size={18} weight="fill" className="text-brand" /> New Driver
              </DialogTitle>
              <DialogDescription className="text-xs">
                Creates a persistent driver link + permanent driver_token. Idempotent on email.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="overline">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="driver-form-name" className="rounded-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="driver-form-email" className="rounded-none" />
                </div>
                <div>
                  <Label className="overline">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="driver-form-phone" className="rounded-none" />
                </div>
              </div>
              <div>
                <Label className="overline">Carrier company</Label>
                <Input value={form.carrier_company} onChange={(e) => setForm({ ...form, carrier_company: e.target.value })}
                  data-testid="driver-form-company" className="rounded-none" />
              </div>
              <div>
                <Label className="overline">Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="driver-form-notes" className="rounded-none" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setAddOpen(false)} className="rounded-none">Cancel</Button>
                <Button onClick={() => { submit(); setAddOpen(false); }} data-testid="driver-form-submit"
                  className="bg-brand text-white rounded-none hover:bg-[#0040d9]">
                  <Clipboard size={14} weight="bold" className="mr-1" /> Create
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
